import { P2GBox, P2GIcon, P2GLink, P2GText, P2GGrid, SvgDrag } from '@parcel2go/core-ui';
import { useCallback, useState } from 'react';
import { ConsignmentApiModel, ConsignmentParcelApiModel } from '~/lib/api/smartsend/types';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { DimensionAndWeightDisplay } from '../../DimensionDisplay';
import { ContentSummaryTable } from '../../ContentSummaryTable';
import { SumNumber } from '~/apps/smartsend/lib/helpers/ArrayHelper';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import { TariffCode } from '../../../customs/TariffCode';
import { TFunction } from '@parcel2go/npm-p2g-app';
import { canDropRowIntoParcel } from '~/apps/smartsend/lib/parcels/planEdits';

export const HideableConsignmentContentsFields = ['TariffCode', 'CountryOfManufacture'];

/**
 * A custom type deliberately, never text/plain: with text/plain set, a drag that misses every parcel
 * card and lands on a text input elsewhere on the page inserts the raw UID into it. Editable targets
 * ignore an unknown type, and any non-empty payload is enough to start the drag.
 */
const DRAG_MIME = 'application/x-p2g-parcel-content';

/**
 * Accepts the drag if it is one of ours, and reports whether it was accepted.
 *
 * Keyed on the dataTransfer TYPE rather than on React state, and it does not consult whether this
 * particular card is a legal target. Both of those were the original bug: the gate read `canDropHere`,
 * which is derived from state set in onDragStart, so the first dragover after a drag begins could see
 * no drag in progress, decline to preventDefault, and leave the browser with no registered drop target -
 * whereupon drop never fired and the whole gesture silently did nothing.
 *
 * dataTransfer.types IS readable during dragover (protected mode hides getData, not types), so this
 * needs no state at all. Legality is enforced once, in onParcelDrop, which is where the write happens.
 */
const acceptDrag = (e: React.DragEvent): boolean => {
  if (!e.dataTransfer.types?.includes(DRAG_MIME)) {
    return false;
  }
  e.preventDefault();
  e.dataTransfer.dropEffect = 'move';
  return true;
};

/** The row being dragged and the card it is currently over. Held by ContentSummaryTableBody. */
interface RowDrag {
  contentUID: string;
  sourceParcelUID: string;
  overParcelUID?: string;
}

interface ParcelDragProps {
  dragEnabled?: boolean;
  drag?: RowDrag | null;
  /** Precomputed per parcel per render - dragover fires continuously and must stay cheap. */
  canDropHere?: boolean;
  onRowDragStart?: (contentUID: string, sourceParcelUID: string) => void;
  onRowDragEnd?: () => void;
  onParcelDragOver?: (targetParcelUID: string) => void;
  onParcelDragLeave?: () => void;
  onParcelDrop?: (targetParcelUID: string) => void;
}

const ParcelView = ({
  pi,
  p,
  keyedParcelContent,
  setExpand,
  t,
  highlightManualContents,
  currencyCode,
  totalParcels,
  hideField,
  dragEnabled,
  drag,
  canDropHere,
  onRowDragStart,
  onRowDragEnd,
  onParcelDragOver,
  onParcelDragLeave,
  onParcelDrop
}: {
  pi: number;
  p: ConsignmentParcelApiModel;
  totalParcels: number;
  currencyCode: string;
  keyedParcelContent?: Record<string, any>;
  setExpand?: (expand: boolean) => void;
  t: TFunction;
  highlightManualContents?: boolean;
  hideField?: (field: string) => boolean;
} & ParcelDragProps) => {
  const parcelValue = p.Contents.reduce((sum, c) => sum + c.Value, 0);

  // Only show the parcel value if there is more than one parcel, the value is greater than zero, and the field is not hidden.
  // Also, only show if there is more than one content item in the parcel.
  const showParcelValue = totalParcels > 1 && parcelValue > 0 && !hideField?.('Value') && p.Contents.length > 1;

  const dropActive = dragEnabled && canDropHere && drag?.overParcelUID === p.UID;

  return (
    <P2GGrid
      item
      key={`${pi}_${p.UID}`}
      data-testid={`parcel-card-${p.UID}`}
      data-drop-active={dropActive || undefined}
      // Both dragenter AND dragover are cancelled. The spec allows either, but browsers differ on
      // which one actually registers the drop target.
      onDragEnter={dragEnabled ? acceptDrag : undefined}
      onDragOver={
        dragEnabled
          ? (e: React.DragEvent) => {
              if (!acceptDrag(e)) {
                return;
              }
              // The highlight still keys off canDropHere, so an illegal card accepts the drag event
              // but never lights up - and onParcelDrop refuses the write.
              if (canDropHere) {
                onParcelDragOver?.(p.UID);
              }
            }
          : undefined
      }
      onDragLeave={
        dragEnabled
          ? (e: React.DragEvent) => {
              // relatedTarget is the node being ENTERED. The rows nest six levels of P2GBox, so
              // every internal boundary fires dragleave - without this the highlight strobes.
              // It is null when the pointer leaves the window, which correctly clears.
              if (e.currentTarget.contains(e.relatedTarget as Node)) {
                return;
              }
              onParcelDragLeave?.();
            }
          : undefined
      }
      onDrop={
        dragEnabled
          ? (e: React.DragEvent) => {
              e.preventDefault();
              onParcelDrop?.(p.UID);
            }
          : undefined
      }
      sx={
        dragEnabled
          ? {
              borderRadius: 1,
              // Reserved at all times, and an outline rather than a border: both are outside the box
              // model, so the transparent -> coloured swap cannot reflow the card.
              outline: '2px solid transparent',
              outlineOffset: 2,
              ...(dropActive && { outlineColor: 'primary.main', backgroundColor: 'action.hover' })
            }
          : undefined
      }
    >
      {keyedParcelContent?.[p.Id]}
      <P2GGrid container alignItems="center" spacing={2}>
        <P2GGrid item flexGrow={1}>
          <P2GText>
            <strong>
              {t('BOX_NUMBER_CAPTION', {
                defaultValue: 'Box {{number}} of {{total}}',
                number: pi + 1,
                total: totalParcels
              })}
            </strong>
          </P2GText>
        </P2GGrid>
        <P2GGrid item flexShrink={1}>
          <P2GText variant="body2">
            <DimensionAndWeightDisplay dimensions={p.Dimensions} localUnitsEntry={p.LocalUnits} variant="body2" />
            {showParcelValue && (
              <>
                {p.Dimensions?.Weight > 0 && <>&nbsp;&nbsp;|&nbsp;&nbsp;</>}
                {toDisplayCurrency(currencyCode, parcelValue)}
              </>
            )}
          </P2GText>
        </P2GGrid>
        {setExpand && (
          <P2GGrid item flexShrink={1}>
            <P2GLink onClick={() => setExpand(true)}>{t('EDIT_BUTTON', 'Edit')}</P2GLink>
          </P2GGrid>
        )}
      </P2GGrid>
      <P2GGrid container spacing={1} mt={1}>
        <P2GGrid item flexGrow={1}>
          <P2GBox sx={{ borderLeft: '3px solid', borderColor: '#f2f2f2', pl: 2, ml: 2, pr: 2 }}>
            {p.Contents.map((c) => (
              <P2GBox
                // UID alone, not `${c.UID}_${ci}`: the index coupling changed the key of every row
                // after a move, remounting subtrees that had not changed.
                key={c.UID}
                data-testid={`content-row-${c.UID}`}
                // On the row rather than the handle - HTML5 DnD drags whatever carries the attribute,
                // so a draggable handle would produce a ghost of just the glyph.
                draggable={dragEnabled || undefined}
                onDragStart={
                  dragEnabled
                    ? (e: React.DragEvent) => {
                        e.dataTransfer.setData(DRAG_MIME, c.UID);
                        e.dataTransfer.effectAllowed = 'move';
                        onRowDragStart?.(c.UID, p.UID);
                      }
                    : undefined
                }
                onDragEnd={dragEnabled ? () => onRowDragEnd?.() : undefined}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'nowrap',
                  gap: 1,
                  alignItems: 'center',
                  py: 1,
                  ...(dragEnabled && {
                    cursor: 'grab',
                    // Without this Chrome starts a TEXT drag from the description instead.
                    userSelect: 'none',
                    '&:hover .p2g-row-drag-handle': { opacity: 1 },
                    ...(drag?.contentUID === c.UID && { opacity: 0.4 })
                  })
                }}
              >
                {dragEnabled && (
                  <P2GBox
                    className="p2g-row-drag-handle"
                    aria-hidden
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      // Space reserved always and only the opacity toggled, or every row reflows on
                      // hover. Hidden where there is no hover, which is also where DnD does not work.
                      opacity: 0,
                      transition: 'opacity 120ms',
                      '@media (hover: none)': { display: 'none' }
                    }}
                  >
                    <P2GIcon size="xs">
                      <SvgDrag />
                    </P2GIcon>
                  </P2GBox>
                )}
                {c.Quantity > 0 && (
                  <P2GBox>
                    <P2GText component="p" variant="body2">
                      {c.Quantity}&nbsp;&nbsp;&nbsp;x
                    </P2GText>
                  </P2GBox>
                )}
                <P2GBox sx={{ flexGrow: 1 }}>
                  <P2GBox
                    sx={{
                      display: 'flex',
                      flexDirection: {
                        xs: 'column',
                        sm: 'row'
                      },
                      flexWrap: {
                        xs: 'nowrap',
                        sm: 'wrap'
                      },
                      gap: 1,
                      alignItems: {
                        xs: 'flex-start',
                        sm: 'center'
                      },
                      py: 1
                    }}
                  >
                    <P2GBox sx={{ flexGrow: 2 }}>
                      <P2GText component="p" variant="body2">
                        {!c.Id && highlightManualContents ? <i>{c.Description}</i> : c.Description}
                      </P2GText>
                      <P2GBox sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', gap: 1 }}>
                        {c.CountryOfManufacture && !hideField('CountryOfManufacture') && (
                          <P2GText component="p" variant="caption">
                            {c.CountryOfManufacture}
                          </P2GText>
                        )}
                        {c.Sku && !hideField('Sku') && (
                          <P2GText component="p" variant="caption">
                            {c.Sku}
                          </P2GText>
                        )}
                      </P2GBox>
                    </P2GBox>
                    {c.Value > 0 && !hideField('Value') && (
                      <P2GBox>
                        <P2GText component="p" variant="body2">
                          {toDisplayCurrency(currencyCode, c.Value)}
                        </P2GText>
                      </P2GBox>
                    )}
                    {c.TariffCode && !hideField('TariffCode') && (
                      <P2GBox>
                        <TariffCode code={c.TariffCode} selectable={false} />
                      </P2GBox>
                    )}
                  </P2GBox>
                </P2GBox>
              </P2GBox>
            ))}
          </P2GBox>
        </P2GGrid>
      </P2GGrid>
    </P2GGrid>
  );
};

export interface props {
  consignment: ConsignmentApiModel;
  setExpand?: (e: boolean) => void;
  currencyCode: string;
  children?: React.ReactNode;
  keyedParcelContent?: Record<string, React.ReactNode>;
  hideTotals?: boolean;
  hiddenRowFields?: string[];
  highlightManualContents?: boolean;
  /**
   * Supply to make content rows draggable between parcel cards. Absent - the default, and every
   * read-only call site including the picking list - renders exactly what it always has: no
   * draggable attribute, no handle, no listeners.
   *
   * Whether a given drop is legal is NOT decided by the caller. This component asks
   * canDropRowIntoParcel against the consignment it was already given, so the highlight and the write
   * can never disagree about what is droppable.
   */
  onDragRowToParcel?: (contentUID: string, targetParcelUID: string) => void;
}

export const ContentSummaryTableBody = ({
  consignment,
  setExpand,
  keyedParcelContent,
  currencyCode,
  t,
  hiddenRowFields,
  highlightManualContents,
  onDragRowToParcel
}: props & { t: TFunction<string, undefined> }) => {
  const hideField = useCallback(
    (field: string) => {
      return hiddenRowFields?.includes(field);
    },
    [hiddenRowFields]
  );

  // Held here rather than in ParcelView because the dragged row and the highlighted target card are
  // in different ParcelViews.
  const [drag, setDrag] = useState<RowDrag | null>(null);
  const dragEnabled = !!onDragRowToParcel;

  const onParcelDrop = (targetParcelUID: string) => {
    // Re-checked rather than trusted: the dragover gate is a browser behaviour, and this is the write.
    if (drag && canDropRowIntoParcel(consignment, drag.contentUID, targetParcelUID)) {
      onDragRowToParcel(drag.contentUID, targetParcelUID);
    }
    // Cleared here as well as on dragEnd - a drop that prunes the source unmounts the source row
    // before dragEnd can reach it.
    setDrag(null);
  };

  const parcels = consignment.Parcels;
  return (
    <P2GGrid container direction="column" spacing={2}>
      {parcels.map((p, pi) => (
        <ParcelView
          key={`p.${p.UID}`}
          p={p}
          pi={pi}
          totalParcels={parcels.length}
          currencyCode={currencyCode}
          highlightManualContents={highlightManualContents}
          keyedParcelContent={keyedParcelContent}
          t={t}
          hideField={hideField}
          setExpand={setExpand}
          dragEnabled={dragEnabled}
          drag={drag}
          canDropHere={!!drag && canDropRowIntoParcel(consignment, drag.contentUID, p.UID)}
          onRowDragStart={(contentUID, sourceParcelUID) => setDrag({ contentUID, sourceParcelUID })}
          onRowDragEnd={() => setDrag(null)}
          onParcelDragOver={(targetParcelUID) =>
            setDrag((current) =>
              !current || current.overParcelUID === targetParcelUID
                ? current
                : { ...current, overParcelUID: targetParcelUID }
            )
          }
          onParcelDragLeave={() =>
            setDrag((current) => (current?.overParcelUID ? { ...current, overParcelUID: undefined } : current))
          }
          onParcelDrop={onParcelDrop}
        />
      ))}
    </P2GGrid>
  );
};

const ConsignmentSummaryParcels: React.FC<props> = ({
  consignment,
  currencyCode,
  setExpand,
  children,
  keyedParcelContent,
  hideTotals,
  hiddenRowFields,
  highlightManualContents,
  onDragRowToParcel
}) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  if (!consignment || !consignment.Parcels || consignment.Parcels.length === 0) {
    return null;
  }

  return (
    <ContentSummaryTable
      totalValue={SumNumber(consignment?.Parcels?.map((p) => p.Contents?.reduce((sum, c) => sum + c.Value, 0)))}
      totalWeight={SumNumber(consignment?.Parcels?.map((p) => p.Dimensions.Weight))}
      currencyCode={currencyCode}
      hideTotals={hideTotals}
    >
      {children}
      <ContentSummaryTableBody
        consignment={consignment}
        setExpand={setExpand}
        keyedParcelContent={keyedParcelContent}
        currencyCode={currencyCode}
        hiddenRowFields={hiddenRowFields}
        highlightManualContents={highlightManualContents}
        onDragRowToParcel={onDragRowToParcel}
        t={t}
      />
    </ContentSummaryTable>
  );
};

export default ConsignmentSummaryParcels;
